import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterGroup from '@/components/feature/FilterGroup';
import Pagination from '@/components/feature/Pagination';
import WorkCard from '@/components/feature/WorkCard';
import { works } from '@/mocks/works';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;

const REGION_OPTIONS = [
  { value: 'all', label: '全部地区' },
  { value: '国产', label: '国产' },
  { value: '欧美', label: '欧美' },
  { value: '日韩', label: '日韩' },
  { value: '港台', label: '港台' },
];

const YEAR_OPTIONS = [
  { value: 'all', label: '全部年份' },
  { value: '2025', label: '2025' },
  { value: '2024', label: '2024' },
  { value: '2023', label: '2023' },
];

const SORT_OPTIONS = [
  { value: 'latest', label: '最新上映' },
  { value: 'rating', label: '评分最高' },
  { value: 'popular', label: '最受欢迎' },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const isTv = type === 'tv';
  const categoryName = isTv ? '电视剧' : '电影';

  const [region, setRegion] = useState('all');
  const [year, setYear] = useState('all');
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);

  useSeo({
    title: `${categoryName}大全 - 美丽小蜜桃3影视大全`,
    description: `这里汇聚最新和经典${categoryName}资源，方便查找与浏览。美丽小蜜桃3影视大全为你整理${categoryName}的剧情介绍、演员阵容与上映信息。`,
    keywords: `美丽小蜜桃3,${categoryName}大全,${categoryName}资源,${categoryName}剧情介绍,最新${categoryName}`,
  });

  const filtered = useMemo(() => {
    const list = works.filter((work) => work.type === (isTv ? 'tv' : 'movie'));
    return list
      .filter((work) => (region === 'all' ? true : work.region === region))
      .filter((work) => (year === 'all' ? true : work.year === Number(year)))
      .sort((a, b) => {
        if (sort === 'rating') return b.rating - a.rating;
        if (sort === 'popular') return b.popularity - a.popularity;
        return b.year - a.year || b.popularity - a.popularity;
      });
  }, [isTv, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setRegion('all');
    setYear('all');
    setSort('latest');
    setPage(1);
  };

  const hasFilter = region !== 'all' || year !== 'all' || sort !== 'latest';

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="container-page py-6 md:py-9">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: `${categoryName}大全` }]} />
          <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">最新{categoryName}大全</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {isTv
              ? '这里汇聚最新和经典的电视剧资源，涵盖国产、欧美、日韩等地区的热播剧集，方便查找与浏览。'
              : '这里汇聚最新和经典的电影资源，涵盖国产、欧美、日韩等地区的院线佳作，方便查找与浏览。'}
          </p>
        </div>
      </section>

      <section className="container-page pt-6 md:pt-8">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <FilterGroup label="地区" options={REGION_OPTIONS} value={region} onChange={updateFilter(setRegion)} />
          <FilterGroup label="年份" options={YEAR_OPTIONS} value={year} onChange={updateFilter(setYear)} />
          <FilterGroup label="排序" options={SORT_OPTIONS} value={sort} onChange={updateFilter(setSort)} />
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 部{categoryName}
          </p>
          {hasFilter && (
            <button
              type="button"
              onClick={resetFilters}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-5 xl:grid-cols-5">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
            <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-16 flex flex-col items-center justify-center py-10 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-movie-line text-3xl" />
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">当前筛选条件下没有找到相关内容</p>
            <p className="mt-1 text-sm text-foreground-500">试着调整地区或年份，或者重置全部筛选条件</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </section>

      <section className="container-page pt-12">
        <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-600">
          <span>相关分类：</span>
          <Link
            to={isTv ? '/category/movie' : '/category/tv'}
            className="cursor-pointer rounded-full bg-secondary-100 px-3.5 py-1.5 text-secondary-900 transition-colors hover:bg-secondary-200"
          >
            {isTv ? '前往电影大全' : '前往电视剧大全'}
          </Link>
          <Link
            to="/about"
            className="cursor-pointer rounded-full bg-background-100 px-3.5 py-1.5 text-foreground-700 transition-colors hover:bg-background-200"
          >
            了解本站
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}